import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Home as HomeIcon,
  AccountBalance as AccountBalanceIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  AttachMoney as AttachMoneyIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { API_URL } from '../../../utils/config';

interface VendorDetails {
  id: string;
  syncToken: string;
  displayName: string;
  givenName?: string;
  middleName?: string;
  familyName?: string;
  suffix?: string;
  title?: string;
  companyName?: string;
  fullyQualifiedName?: string;
  printOnCheckName?: string;
  isActive: boolean;

  contact: {
    primaryPhone?: string;
    alternatePhone?: string;
    mobilePhone?: string;
    fax?: string;
    primaryEmail?: string;
    website?: string;
    otherContactInfo?: any;
  };

  billingAddress?: {
    line1?: string;
    line2?: string;
    line3?: string;
    line4?: string;
    line5?: string;
    city?: string;
    country?: string;
    countrySubDivisionCode?: string;
    postalCode?: string;
    latitude?: number;
    longitude?: number;
  };

  financial: {
    balance?: number;
    openBalanceDate?: string;
    currencyCode?: string;
    currencyName?: string;
    billingRate?: number;
    costRate?: number;
    outstandingInvoicesByCurrency?: Array<{
      currencyCode: string;
      totalOutstanding: number;
      invoiceCount: number;
    }>;
    pendingPaymentsByCurrency?: Array<{
      currencyCode: string;
      totalPending: number;
      billCount: number;
    }>;
  };

  taxCompliance: {
    vendor1099?: boolean;
    taxIdentifier?: string;
    businessNumber?: string;
    t5018Eligible?: boolean;
    defaultTaxCode?: {
      value: string;
      name: string;
    };
    tdsEnabled?: boolean;
    tdsEntityTypeId?: number;
    tdsSectionTypeId?: number;
    tdsOverrideThreshold?: boolean;
    gstin?: string;
    gstRegistrationType?: string;
  };

  references: {
    terms?: {
      value: string;
      name: string;
    };
    vendorType?: {
      value: string;
      name: string;
    };
    parent?: {
      value: string;
      name: string;
    };
    prefillAccount?: {
      value: string;
      name: string;
    };
  };

  system: {
    domain?: string;
    sparse?: boolean;
    intuitId?: string;
    v4IdPseudonym?: string;
  };

  metadata: {
    quickBooksCreateTime?: string;
    quickBooksLastUpdatedTime?: string;
    platformCreatedAt?: string;
    platformUpdatedAt?: string;
    syncStatus?: string;
    lastSyncAt?: string;
    syncError?: string;
  };

  recentBills: Array<{
    id: string;
    docNumber?: string;
    transactionDate?: string;
    dueDate?: string;
    totalAmount?: number;
    balance?: number;
    currencyCode?: string;
    privateNote?: string;
    subsidiaryInvoiceNumber?: string;
    invoice?: {
      id?: string;
      docNumber?: string;
      transactionDate?: string;
      customerId?: string;
      customerName?: string;
      totalAmount?: number;
      balance?: number;
      currencyCode?: string;
      currencyName?: string;
      status?: string;
    } | null;
  }>;

  associatedCustomers: Array<{
    id: string;
    displayName: string;
    companyName?: string;
    givenName?: string;
    familyName?: string;
    primaryPhone?: string;
    primaryEmail?: string;
    balance?: number;
    currencyCode?: string;
    isActive?: boolean;
    associatedAt?: string;
  }>;

  associatedUsers: Array<{
    id: string;
    email: string;
    name?: string;
    associatedAt?: string;
  }>;
}

export const VendorDetailsPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { hasPermission } = useUserPermissions();
  const [vendor, setVendor] = useState<VendorDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVendorDetails = async () => {
    if (!vendorId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/${vendorId}`,
        {
          timeout: 30000 // 30 seconds timeout
        }
      );

      if (!response || !response.ok) {
        if (response?.status === 403) {
          throw new Error('Access denied. You do not have permission to view this vendor.');
        }
        if (response?.status === 404) {
          throw new Error('Vendor not found');
        }
        throw new Error('Failed to fetch vendor details');
      }

      const data = await response.json();
      setVendor(data.data);
    } catch (err: any) {
      console.error('Error fetching vendor details:', err);
      setError(err.message || 'Failed to fetch vendor details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorDetails();
  }, [vendorId]);

  const formatCurrency = (amount?: number, currency: string = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!vendor) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">
          No vendor data available.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        {hasPermission('accounting.admin') && (
          <Link
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/hub/accounting/vendors')}
          >
            Vendors
          </Link>
        )}
        <Typography color="text.primary">{vendor.displayName}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => navigate('/admin/hub/accounting/vendors')}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <AccountBalanceIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                {vendor.displayName}
              </Typography>
              {vendor.companyName && vendor.companyName !== vendor.displayName && (
                <Typography variant="body1" color="text.secondary">
                  {vendor.companyName}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={vendor.isActive ? 'Active' : 'Inactive'}
              color={vendor.isActive ? 'success' : 'default'}
            />
            {vendor.taxCompliance.vendor1099 && (
              <Chip label="1099" color="warning" />
            )}
            <IconButton onClick={fetchVendorDetails} title="Refresh">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              {(vendor.givenName || vendor.familyName) && (
                <ListItem sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ mt: 0.5 }}>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Name"
                    secondary={
                      <Box component="span">
                        {[vendor.givenName, vendor.familyName].filter(Boolean).join(' ')}
                        {vendor.contact.primaryEmail && (
                          <><br />{vendor.contact.primaryEmail}</>
                        )}
                        {vendor.contact.mobilePhone && (
                          <><br />{vendor.contact.mobilePhone}</>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              )}
              {vendor.contact.primaryPhone && (
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Primary Phone"
                    secondary={vendor.contact.primaryPhone}
                  />
                </ListItem>
              )}
              {vendor.contact.alternatePhone && (
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Alternate Phone"
                    secondary={vendor.contact.alternatePhone}
                  />
                </ListItem>
              )}
              {vendor.contact.fax && (
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Fax"
                    secondary={vendor.contact.fax}
                  />
                </ListItem>
              )}
              {vendor.contact.website && (
                <ListItem>
                  <ListItemIcon>
                    <LanguageIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Website"
                    secondary={vendor.contact.website}
                  />
                </ListItem>
              )}
              {vendor.billingAddress && (
                <ListItem sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ mt: 0.5 }}>
                    <LocationIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Billing Address"
                    secondary={
                      <Box component="span">
                        {vendor.billingAddress.line1 && (
                          <>{vendor.billingAddress.line1}<br /></>
                        )}
                        {vendor.billingAddress.line2 && (
                          <>{vendor.billingAddress.line2}<br /></>
                        )}
                        {vendor.billingAddress.line3 && (
                          <>{vendor.billingAddress.line3}<br /></>
                        )}
                        {(vendor.billingAddress.city || vendor.billingAddress.countrySubDivisionCode || vendor.billingAddress.postalCode) && (
                          <>
                            {[
                              vendor.billingAddress.city,
                              vendor.billingAddress.countrySubDivisionCode,
                              vendor.billingAddress.postalCode
                            ].filter(Boolean).join(', ')}<br />
                          </>
                        )}
                        {vendor.billingAddress.country}
                      </Box>
                    }
                  />
                </ListItem>
              )}
            </List>

            {!vendor.contact.primaryEmail && !vendor.contact.primaryPhone && !vendor.contact.website && !vendor.billingAddress && (
              <Typography variant="body2" color="text.secondary">
                No contact information available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Right Column - Financial and Tax */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
            {/* Outstanding Invoices */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Outstanding Invoices
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {vendor.financial.outstandingInvoicesByCurrency && vendor.financial.outstandingInvoicesByCurrency.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {vendor.financial.outstandingInvoicesByCurrency.map((item) => (
                      <Box key={item.currencyCode} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.currencyCode} ({item.invoiceCount} invoice{item.invoiceCount !== 1 ? 's' : ''})
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ color: item.totalOutstanding > 0 ? 'success.main' : 'text.primary' }}>
                          {formatCurrency(item.totalOutstanding, item.currencyCode)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No outstanding invoices
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Pending Payments */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Pending Payments
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {vendor.financial.pendingPaymentsByCurrency && vendor.financial.pendingPaymentsByCurrency.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {vendor.financial.pendingPaymentsByCurrency.map((item) => (
                      <Box key={item.currencyCode} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.currencyCode} ({item.billCount} bill{item.billCount !== 1 ? 's' : ''})
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ color: item.totalPending > 0 ? 'success.main' : 'text.primary' }}>
                          {formatCurrency(item.totalPending, item.currencyCode)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No pending payments
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Tax & Compliance */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Tax & Compliance
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">1099 Vendor</Typography>
                    <Typography variant="body1">
                      {vendor.taxCompliance.vendor1099 ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  {vendor.taxCompliance.taxIdentifier && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Tax Identifier</Typography>
                      <Typography variant="body1">
                        {vendor.taxCompliance.taxIdentifier}
                      </Typography>
                    </Grid>
                  )}
                  {vendor.taxCompliance.businessNumber && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Business Number</Typography>
                      <Typography variant="body1">
                        {vendor.taxCompliance.businessNumber}
                      </Typography>
                    </Grid>
                  )}
                  {vendor.taxCompliance.defaultTaxCode && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Default Tax Code</Typography>
                      <Typography variant="body1">
                        {vendor.taxCompliance.defaultTaxCode.name}
                      </Typography>
                    </Grid>
                  )}
                  {vendor.taxCompliance.gstin && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">GSTIN</Typography>
                      <Typography variant="body1">
                        {vendor.taxCompliance.gstin}
                      </Typography>
                    </Grid>
                  )}
                  {vendor.taxCompliance.gstRegistrationType && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">GST Registration Type</Typography>
                      <Typography variant="body1">
                        {vendor.taxCompliance.gstRegistrationType}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Recent Business */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Recent Business
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<BusinessIcon />}
                onClick={() => navigate(`/admin/hub/accounting/vendors/${vendorId}/business`)}
              >
                Manage Business
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {vendor.recentBills.length > 0 ? (
              <TableContainer sx={{ maxHeight: 300, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>HHTL Invoice #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Invoice Balance</TableCell>
                      <TableCell align="center" sx={{ width: 40 }}></TableCell>
                      <TableCell>HHTL Bill #</TableCell>
                      <TableCell>Subsidiary Inv#</TableCell>
                      <TableCell align="right">Bill Balance</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendor.recentBills
                      .slice(0, 8)
                      .map((bill) => (
                      <TableRow key={bill.id}>
                        <TableCell>
                          {bill.invoice?.docNumber ? (
                            <Chip
                              label={bill.invoice.docNumber}
                              size="small"
                              clickable
                              onClick={() => navigate(`/admin/hub/accounting/invoices/${bill.invoice?.id}`)}
                              sx={{
                                fontFamily: 'monospace',
                                backgroundColor: 'rgba(0, 45, 96, 0.08)',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 45, 96, 0.15)',
                                }
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ color: 'rgb(0, 45, 96)' }}>
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'rgb(0, 45, 96)' }}>
                            {bill.invoice?.transactionDate ? formatDate(bill.invoice.transactionDate) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {bill.invoice?.customerId && bill.invoice?.customerName ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => navigate(`/admin/hub/accounting/customers/${bill.invoice?.customerId}`)}
                              sx={{ color: 'rgb(0, 45, 96)', textAlign: 'left' }}
                            >
                              {bill.invoice.customerName}
                            </Link>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'rgb(0, 45, 96)' }}>
                              {bill.invoice?.customerName || '—'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {bill.invoice?.balance !== undefined ? (
                            <Typography
                              variant="body2"
                              sx={{
                                color: bill.invoice.balance === 0 ? 'green' :
                                       bill.invoice.balance > 0 ? 'error.main' :
                                       'text.primary'
                              }}
                            >
                              {formatCurrency(bill.invoice.balance, bill.invoice.currencyCode)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ width: 40 }}>
                          {bill.invoice && (
                            <Tooltip title="Bill linked to Invoice">
                              <LinkIcon sx={{ fontSize: 18, color: 'rgb(0, 45, 96)' }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={bill.id}
                            size="small"
                            clickable
                            onClick={() => navigate(`/admin/hub/accounting/bills/${bill.id}`)}
                            sx={{
                              fontFamily: 'monospace',
                              backgroundColor: 'rgba(25, 118, 210, 0.08)',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.15)',
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {bill.subsidiaryInvoiceNumber || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              color: bill.balance === 0 ? 'green' :
                                     bill.balance && bill.balance > 0 ? 'error.main' :
                                     'text.primary'
                            }}
                          >
                            {formatCurrency(bill.balance, bill.currencyCode)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(bill.invoice?.balance === 0 && bill.balance !== 0 && bill.balance !== null && bill.balance !== undefined) ? (
                            <Chip
                              label="PAY BILL"
                              size="small"
                              color="error"
                              sx={{ fontWeight: 600 }}
                            />
                          ) : bill.invoice?.status ? (
                            <Chip
                              label={bill.invoice.status.toUpperCase()}
                              size="small"
                              color={
                                bill.invoice.status === 'paid' ? 'success' :
                                bill.invoice.status === 'void' ? 'default' :
                                'warning'
                              }
                              sx={{ fontWeight: 600 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No recent bills found for this vendor.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Associated Customers */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Associated Customers
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<PeopleIcon />}
                onClick={() => navigate(`/admin/hub/accounting/vendors/${vendorId}/customers`)}
              >
                View All Customers
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {vendor.associatedCustomers && vendor.associatedCustomers.length > 0 ? (
              <TableContainer sx={{ maxHeight: 300, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Display Name</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Primary Phone</TableCell>
                      <TableCell>Primary Email</TableCell>
                      <TableCell align="right">Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendor.associatedCustomers.map((customer) => (
                      <TableRow
                        key={customer.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => navigate(`/admin/hub/accounting/customers/${customer.id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {customer.displayName}
                          </Typography>
                        </TableCell>
                        <TableCell>{customer.companyName || '-'}</TableCell>
                        <TableCell>{customer.primaryPhone || '-'}</TableCell>
                        <TableCell>{customer.primaryEmail || '-'}</TableCell>
                        <TableCell align="right">
                          {customer.balance !== undefined && customer.balance !== null ? (
                            <Typography
                              variant="body2"
                              sx={{
                                color: customer.balance === 0 ? 'green' : 'error.main'
                              }}
                            >
                              {formatCurrency(customer.balance, customer.currencyCode)}
                            </Typography>
                          ) : (
                            <Typography variant="body2">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No customers are associated with this vendor. To associate customers, use the vendor-customer relationship table.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Associated Users */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Associated Users
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {vendor.associatedUsers.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Associated At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendor.associatedUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name || '-'}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{formatDateTime(user.associatedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No users are associated with this vendor. To associate users, visit the{' '}
                <Link
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate('/admin/hub/accounting/users')}
                >
                  User Access Control
                </Link>{' '}
                page.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};